import { useIsMutating } from "@tanstack/react-query";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { Link, matchPath } from "react-router-dom";

import { Msg } from "@shared/components";
import {
  examResultKeys,
  useSubmitApplicantsExamsResults,
} from "@shared/hooks/query";
import {
  ApplicantChallengeTimerModel,
  ApplicantExamModel,
} from "@shared/models";
import { secondsOf5Minutes } from "@shared/services/date";
import { ChallengeStyle, SubmissionType } from "@shared/services/enums";

interface AutoSubmittedChallengeProps {
  challenge: ApplicantChallengeTimerModel;
  exam: ApplicantExamModel;
  isExamTimeout: boolean;
  addNotification: (item: React.ReactElement) => void;
  onChallengeTimeUp?: (challengeId: number) => void;
}

export const AutoSubmittedChallenge = ({
  challenge,
  exam,
  isExamTimeout,
  addNotification,
  onChallengeTimeUp,
}: AutoSubmittedChallengeProps) => {
  const { challengeId, seconds } = challenge;
  const challengeResultId = exam.getResultByChallengeId(challengeId).id;
  const isSubmitting = Boolean(
    useIsMutating(examResultKeys.submit(challengeResultId)),
  );
  const submitApplicantsExamsResults =
    useSubmitApplicantsExamsResults(challengeResultId);

  const handleChallenge5MinutesLeft = useCallback(
    (challengeId: number) => {
      const challenge = exam.getChallengeByChallengeId(challengeId);
      addNotification(
        <span>
          <Msg
            id={"message.applicant.challengeTimeupBefore5mins"}
            values={{
              challenge: challenge.title,
            }}
          />
          {matchPath(window.location.pathname, {
            path: `/:orgname/exams/:token/challenges/${challengeId}/:currentPage?`,
            exact: true,
          }) === null && (
            <Link
              to={`/${exam.org.name}/exams/${exam.urlToken}/challenges/${
                challenge.id
              }${challenge.style === ChallengeStyle.Quiz ? "/1" : ""}`}
            >
              <Msg id={"applicant-challenge-link.updateAnswer"} />
            </Link>
          )}
        </span>,
      );
    },
    [addNotification, exam],
  );

  const handleChallengeTimeUp = useCallback(
    (challengeId: number) => {
      const challenge = exam.getChallengeByChallengeId(challengeId);

      if (exam.canChallengeSubmit(challengeId) && !isSubmitting) {
        submitApplicantsExamsResults.mutate({
          examId: exam.id,
          data: { submissionType: SubmissionType.Deadline },
        });

        if (
          matchPath(window.location.pathname, {
            path: `/:orgname/exams/:token/challenges/${challengeId}/:currentPage?`,
            exact: true,
          })
        ) {
          onChallengeTimeUp?.(challengeId);
        }

        addNotification(
          <Msg
            id={"message.applicant.challengeTimeup"}
            values={{
              challenge: challenge.title,
            }}
          />,
        );
      }
    },
    [
      addNotification,
      exam,
      isSubmitting,
      onChallengeTimeUp,
      submitApplicantsExamsResults,
    ],
  );

  useEffect(() => {
    // handle time up and 5 mins left
    if (!isExamTimeout && seconds === secondsOf5Minutes) {
      handleChallenge5MinutesLeft(challenge.challengeId);
    }
    if (!isExamTimeout && seconds === 0) {
      handleChallengeTimeUp(challenge.challengeId);
    }
  }, [
    challenge.challengeId,
    handleChallenge5MinutesLeft,
    handleChallengeTimeUp,
    isExamTimeout,
    onChallengeTimeUp,
    seconds,
    submitApplicantsExamsResults,
  ]);

  return null;
};
